import React from 'react';

import { IWidgetCard } from '@entities/widget-card/model/types';
import { CustomModal, Flex } from '@shared/ui';

import { useInvoiceFields, usePaymentWidgetModal } from './model';
import cls from './PaymentWidgetModal.module.css';
import { PaymentMethods, PaymentPeriods, PaymentSummary, InvoiceFields, GiftBlock } from './ui';

interface IPaymentWidgetModalProps {
    onModalClose: () => void;
    isOpen: boolean;
    currentWidgetId: IWidgetCard['id'] | null;
    fromWidgetPage?: boolean;
}

export const PaymentWidgetModal = ({
    onModalClose,
    isOpen,
    currentWidgetId,
    fromWidgetPage = false,
}: IPaymentWidgetModalProps) => {
    const {
        paymentData,
        paymentContext,
        availablePaymentMethods,
        isPending,
        isPayByCardPending,
        isTrialPending,
        trialInfo,
        activePaymentMethodType,
        activePeriodPicked,
        autoRenewal,
        paymentPeriodInfo,
        payByCardHandler,
        onMethodPick,
        onPeriodPick,
        onEnableWidgetTrialPeriod,
        toggleAutoRenewal,
        trackPayment,
        t,
    } = usePaymentWidgetModal({ currentWidgetId, isModalOpen: isOpen, fromWidgetPage });
    const { companyName, innKpp, handleCompanyNameChange, handleInnKppChange, onInvoiceHandler } =
        useInvoiceFields({
            isModalOpen: isOpen,
            activePeriodPicked,
            paymentContext,
        });
    const actionBtnHandler =
        activePaymentMethodType === 'invoice' ? onInvoiceHandler : payByCardHandler;

    return (
        <CustomModal
            isOpen={isOpen}
            onHideModal={onModalClose}
            closeButtonClass={cls.closeBtn}
            isLoading={isPending}
            contentClasses={cls.modalContent}
        >
            <CustomModal.Header>
                {t('WidgetShop.paymentModal.title')} - {paymentData?.title}
            </CustomModal.Header>
            <CustomModal.Body classes={cls.modalBody}>
                <Flex align="stretch" gap={12}>
                    <Flex direction="column" gap={16} fullWidth>
                        <PaymentPeriods
                            periodsData={paymentData?.widgetPlans}
                            activePeriodPicked={activePeriodPicked}
                            onPeriodPick={onPeriodPick}
                            currencySign={paymentData?.currencySign}
                        />
                        <PaymentMethods
                            paymentsMethods={availablePaymentMethods}
                            activePaymentMethodType={activePaymentMethodType}
                            onMethodPick={onMethodPick}
                        />
                        {activePaymentMethodType === 'invoice' && (
                            <InvoiceFields
                                companyName={companyName}
                                innKpp={innKpp}
                                handleCompanyNameChange={handleCompanyNameChange}
                                handleInnKppChange={handleInnKppChange}
                            />
                        )}
                    </Flex>
                    <PaymentSummary
                        paymentPeriodInfo={paymentPeriodInfo}
                        activePaymentMethodType={activePaymentMethodType}
                        autoRenewal={autoRenewal}
                        toggleAutoRenewal={toggleAutoRenewal}
                        currency={paymentData?.currencySign}
                        actionBtnHandler={() => {
                            trackPayment();
                            actionBtnHandler();
                        }}
                        isActionBtnPending={
                            activePaymentMethodType === 'card' &&
                            (isPayByCardPending || isTrialPending)
                        }
                    />
                </Flex>

                {trialInfo?.isTrialAvailable && (
                    <GiftBlock
                        days={trialInfo.trialDays}
                        onActionClick={onEnableWidgetTrialPeriod}
                    />
                )}
            </CustomModal.Body>
        </CustomModal>
    );
};
